import { type MouseEvent, useState } from 'react';
import { getActionAriaLabel, getActionLabel } from '@/shared/copy/actions';
import { Text } from '@/ui';
import { Box, IconButton, Menu, MenuItem, Tooltip } from '@/ui/primitives';
import {
  DeleteFilledIcon as DeleteIcon,
  EditOutlinedIcon,
  LocalOfferIcon,
  MoreVertIcon,
  RestoreFromTrashOutlinedIcon,
} from '@/ui/primitives/icons';
import { resolveTableActionIconAccentColor } from './tableMetricAccentStyles';
import {
  buildTableActionsCellFrameSx,
  buildTableActionsTriggerSx,
  tableActionsMenuItemSx,
  tableActionsMenuListSx,
  tableActionsMenuPaperSx,
  tableActionsTriggerWrapperSx,
  tableOverlaySurfaceSx,
} from './tablePanelSurfaceSx';
import { TableRowTagsDialog } from './TableRowTagsDialog';
import type { TableActionsCellProps } from './TableCells.types';

export const TableActionsCell = ({
  rowName,
  verticalAlign = 'top',
  disabledTooltip = null,
  editActionLabel = null,
  deleteActionLabel = getActionLabel('delete'),
  editTagsEntityNoun = 'test',
  rowTags = [],
  rowTagColors = [],
  showEditTagsAction = true,
  showEditTagsWithEditAction = false,
  onEdit,
  disableEdit = false,
  onUpdateTagsColor,
  updateTagsColorLoading = false,
  onDelete,
  disableDelete = false,
  onRestore,
  disableRestore = false,
  restoreActionLabel = getActionLabel('restore'),
}: TableActionsCellProps): JSX.Element => {
  const resolvedRowName = rowName.trim() || 'this test';
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [editTagsOpen, setEditTagsOpen] = useState(false);
  const menuOpen = Boolean(anchorEl);
  const actionsDisabled = disabledTooltip !== null;
  const shouldShowEditTagsAction =
    showEditTagsAction && (showEditTagsWithEditAction || onEdit === undefined);

  const handleOpen = (event: MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  return (
    <Box sx={buildTableActionsCellFrameSx(verticalAlign)}>
      <Tooltip
        title={disabledTooltip ?? ''}
        disableHoverListener={!actionsDisabled}
        placement="top"
      >
        <Box component="span" sx={tableActionsTriggerWrapperSx}>
          <IconButton
            aria-label={getActionAriaLabel('rowActions', rowName)}
            aria-haspopup={actionsDisabled ? undefined : 'menu'}
            aria-expanded={!actionsDisabled && menuOpen ? 'true' : undefined}
            size="medium"
            onClick={actionsDisabled ? undefined : handleOpen}
            disabled={actionsDisabled}
            sx={buildTableActionsTriggerSx(actionsDisabled)}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={!actionsDisabled && menuOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: [tableOverlaySurfaceSx, tableActionsMenuPaperSx],
          },
          list: {
            sx: tableActionsMenuListSx,
          },
        }}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {onEdit && editActionLabel ? (
          <MenuItem
            disabled={disableEdit}
            sx={tableActionsMenuItemSx}
            onClick={(event) => {
              event.stopPropagation();
              handleClose();
              onEdit();
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
            <Text role="body">{editActionLabel}</Text>
          </MenuItem>
        ) : null}
        {shouldShowEditTagsAction ? (
          <MenuItem
            disabled={updateTagsColorLoading}
            sx={tableActionsMenuItemSx}
            onClick={(event) => {
              event.stopPropagation();
              handleClose();
              setEditTagsOpen(true);
            }}
          >
            <LocalOfferIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
            <Text role="body">
              {onEdit === undefined
                ? (editActionLabel ?? getActionLabel('editTags'))
                : getActionLabel('editTags')}
            </Text>
          </MenuItem>
        ) : null}
        {onRestore ? (
          <MenuItem
            disabled={disableRestore}
            sx={tableActionsMenuItemSx}
            onClick={(event) => {
              event.stopPropagation();
              handleClose();
              onRestore();
            }}
          >
            <RestoreFromTrashOutlinedIcon
              sx={{
                fontSize: (theme) => theme.app.iconSizes.sm,
                color: (theme) => resolveTableActionIconAccentColor(theme, 'success'),
              }}
            />
            <Text role="body">{restoreActionLabel}</Text>
          </MenuItem>
        ) : onDelete ? (
          <MenuItem
            disabled={disableDelete}
            sx={tableActionsMenuItemSx}
            onClick={(event) => {
              event.stopPropagation();
              handleClose();
              onDelete();
            }}
          >
            <DeleteIcon
              htmlColor="currentColor"
              sx={{
                fontSize: (theme) => theme.app.iconSizes.sm,
                color: (theme) => resolveTableActionIconAccentColor(theme, 'error'),
              }}
            />
            <Text role="body">{deleteActionLabel}</Text>
          </MenuItem>
        ) : null}
      </Menu>
      <TableRowTagsDialog
        open={editTagsOpen}
        loading={updateTagsColorLoading}
        onClose={() => {
          if (updateTagsColorLoading) return;
          setEditTagsOpen(false);
        }}
        onConfirm={onUpdateTagsColor}
        editTagsEntityNoun={editTagsEntityNoun}
        rowName={resolvedRowName}
        rowTagColors={rowTagColors}
        rowTags={rowTags}
      />
    </Box>
  );
};

export { TableCellContainer } from './TableSharedCells';
